import { useEvent } from "client-util/useEvent";
import { useMovableContext } from "components/Movables/MovableProvider";
import {
  getBaseRect,
  posRelFrom,
  posToRatio,
  useMovableToRect,
  clampRect,
  snapRatio,
} from "components/Movables/pos";
import {
  MovableType,
  getBoxId,
  getPieceId,
} from "sockapi/scope-specs/round-spec";
import {
  useUpdateMovable,
  useUpdateMovableNoCommit,
  useCommit,
  useGetRoundStateNonReactive,
  useTransferPiece,
  useUpdatePieceInside,
} from "stores/RoundStore";

/** for force rerendering when resized */
export function useOnDragEnd() {
  const getRoundStateNonReactive = useGetRoundStateNonReactive();
  const updateMovable = useUpdateMovable();
  const updateMovableNoCommit = useUpdateMovableNoCommit();
  const commit = useCommit();
  const ctx = useMovableContext();
  const toRect = useMovableToRect();
  const transfer = useTransferPiece();
  const updateInside = useUpdatePieceInside();

  return useEvent(
    ({
      source,
      target,
      dPos,
    }: {
      source: string;
      target: string;
      dPos?: { x: number; y: number };
    }) => {
      (async () => {
        const base = getBaseRect();
        if (dPos === null || getRoundStateNonReactive === null || base === null)
          return;

        const { derived } = getRoundStateNonReactive();
        const s = derived.movables[source]!;
        const sNewPos = posRelFrom(toRect(s, base), dPos);
        const t = target === "ambient-box" ? null : derived.movables[target]!;
        const tRect =
          t !== null && ctx.type === "round" ? toRect(t, base) : base;
        const sNew = {
          ...snapRatio(
            posToRatio(
              t === null ? ctx.round!.gridCols : t.cols,
              t === null ? ctx.round!.gridRows : t.rows,
              clampRect(sNewPos, tRect),
              tRect
            )
          ),
          rot: s.rot,
          ...(s.flipped !== undefined ? { flipped: s.flipped } : {}),
        };

        if (s.type === MovableType.BOX) {
          await updateMovable(s.id, sNew);
          return;
        }

        if (s.type === MovableType.BOX_PIECE) {
          if (ctx.type === "puzzle") {
            await updateMovable(s.id, sNew);
            return;
          }
          if (!t) {
            updateMovableNoCommit(getPieceId(s), sNew);
            updateMovableNoCommit(s.id, { active: false });
            await commit();
            return;
          }
          await transfer({
            pieceId: getPieceId(s),
            fromBoxId: getBoxId(s),
            toBoxId: t.id,
            props: sNew,
          });
          return;
        }

        if (!t) {
          await updateMovable(s.id, sNew);
          return;
        }
        await updateInside({
          pieceId: s.id,
          boxId: t.id,
          add: true,
          props: sNew,
        });
      })().catch(console.error);
    }
  );
}

const getParents = function* (el: Element) {
  while (el) {
    yield el;
    el = el.parentElement!;
  }
};

export function makeClickOrDispatch() {
  /**
   * If the piece has a transparent background, only allow dragging if our
   * cursor is on top of exactly one image under the puzzle content, and
   * the pixel for that image is not transparent. If this isn't the case, this
   * function should return the next element to send the event to.
   */
  return function clickOrDispatch(element: Element | null, event: MouseEvent) {
    const absPos = {
      x: Math.floor(event.clientX),
      y: Math.floor(event.clientY),
    };
    const allPuzzleElts = document
      .elementsFromPoint(absPos.x, absPos.y)
      .flatMap((el) => {
        for (const parent of getParents(el)) {
          if (parent.classList.contains("puzzle-content")) {
            const isOurs = Array.from(getParents(parent)).includes(element!);
            return [[el, isOurs] as const];
          }
        }
        return [];
      });
    const ourImgs = allPuzzleElts.filter(
      ([el, isOurs]) => el.tagName.toLowerCase() === "img" && isOurs
    );
    const ourLastEltIndex = allPuzzleElts.findLastIndex(([, isOurs]) => isOurs);
    const nextElt =
      ourLastEltIndex !== -1
        ? allPuzzleElts[ourLastEltIndex + 1]?.[0]
        : undefined;
    if (ourImgs.length !== 1) {
      return { allow: false, nextElt };
    }

    const img = ourImgs[0][0] as HTMLImageElement;
    const { deg, flip } =
      Array.from(getParents(img), (p) => {
        if (p.classList.contains("piece-view")) {
          const deg = Number(
            (p as HTMLElement).style.transform.match(
              /rotate\((-?[0-9]+)deg\)/
            )?.[1]
          );
          const scale = Number(
            (p as HTMLElement).style.transform.match(
              /scaleX\(([-0-9.]+)\)/
            )?.[1]
          );
          return {
            deg,
            flip: !!scale && scale < 0,
          };
        }
      }).find(Boolean) || {};
    const rect = img.getBoundingClientRect();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    canvas.width = rect.width;
    canvas.height = rect.height;
    if (flip) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    if (deg) {
      ctx.translate(rect.width / 2, rect.height / 2);
      ctx.rotate((deg * Math.PI) / 180);
      if (deg % 180 !== 0) {
        ctx.translate(-rect.height / 2, -rect.width / 2);
      } else {
        ctx.translate(-rect.width / 2, -rect.height / 2);
      }
    }
    ctx.drawImage(
      img,
      0,
      0,
      deg && deg % 180 !== 0 ? rect.height : rect.width,
      deg && deg % 180 !== 0 ? rect.width : rect.height
    );
    const relPos = {
      x: absPos.x - rect.left,
      y: absPos.y - rect.top,
    };
    const isTransparent =
      ctx.getImageData(relPos.x, relPos.y, 1, 1).data[3] < 128;
    canvas.remove();

    return isTransparent ? { allow: false, nextElt } : { allow: true };
  };
}
